<script setup lang="ts">
const { t: $t } = useI18n()

import { isAfter } from 'date-fns'
import type { ApiExternalReservationGetAllByMachineryId } from '~/types'

const props = defineProps<{
  machineryId: string
}>()

const confirmDeleteReservationId = shallowRef<string>()
const showPartnerSaleReservationCommentsId = shallowRef<string>()
const { externalReservation: queryExternalReservation } = useQuery()
const { serverSidePaginationTableProps, serverSidePagination, watchForPageCount } = useTablePagination({ pageCount: 1 })
const { data: externalReservationsData, isLoading: isLoadingExternalReservations } = queryExternalReservation.allByMachineryId(props.machineryId, serverSidePagination)
const externalReservations = computed(() => externalReservationsData.value?.data)

watchForPageCount(computed(() => externalReservationsData.value?.totalCount ?? 0))

const { $trpc, queryClient, useMutation, makeTrpcErrorToast } = useMutationHelpers()
const notification = useNotification()

const { mutate: mutateDeleteReservation } = useMutation({
  mutationFn: $trpc.externalReservation.delete.mutate,
  onError: makeTrpcErrorToast(notification, { description: $t('machinery.detailsPage.externalReservations.notification.delete.error') }),
  onSuccess: () => {
    queryClient.invalidateQueries({ queryKey: ['externalReservation', 'allByMachineryId', props.machineryId] })
    notification.success({ title: $t('machinery.detailsPage.externalReservations.notification.delete.success'), duration: 4500 })
  },
})

const { externalReservation: { columns } } = useTableColumnConfigs()

function actionButtons(row: ApiExternalReservationGetAllByMachineryId): Action[] {
  if ((row.expiresAt && isAfter(new Date(), new Date(row.expiresAt))) || row.deletedAt) {
    return ['showComments']
  }

  return ['showComments', 'delete']
}

function rowClassName(row: ApiExternalReservationGetAllByMachineryId) {
  if (typeof row.id !== 'string') {
    return ''
  }

  if (row.deletedAt) {
    return 'isDeleted'
  }

  if (row.expiresAt && isAfter(new Date(), new Date(row.expiresAt))) {
    return 'isExpired'
  }

  return 'isActive'
}

function deleteReservation() {
  if (confirmDeleteReservationId.value) {
    mutateDeleteReservation({ id: confirmDeleteReservationId.value })
  }
}
</script>

<template>
  <TheConfirmPopup v-if="confirmDeleteReservationId" @confirm="deleteReservation" @close="confirmDeleteReservationId = undefined">
    {{ $t('machinery.detailsPage.externalReservations.confirm.description') }}
  </TheConfirmPopup>

  <ExternalReservationCommentsPopup
    v-if="showPartnerSaleReservationCommentsId"
    :partner-sale-reservation-id="showPartnerSaleReservationCommentsId"
    @close="showPartnerSaleReservationCommentsId = undefined"
  />

  <div class="mb-4">
    <div class="flex flex-col gap-2">
      <div class="flex items-center gap-2">
        <div class="w-6 h-6 bg-green-100" />
        <span>{{ $t('machinery.detailsPage.externalReservations.legend.active') }}</span>
      </div>
      <div class="flex items-center gap-2">
        <div class="w-6 h-6 bg-red-100" />
        <span>{{ $t('machinery.detailsPage.externalReservations.legend.deleted') }}</span>
      </div>
      <div class="flex items-center gap-2">
        <div class="w-6 h-6 bg-gray-100" />
        <span>{{ $t('machinery.detailsPage.externalReservations.legend.expired') }}</span>
      </div>
    </div>
  </div>

  <div class="listContainer">
    <TableView
      :data="externalReservations"
      v-bind="serverSidePaginationTableProps"
      :columns="columns"
      :is-loading="isLoadingExternalReservations"
      :action-buttons
      :row-class-name
      @delete="({ id }) => confirmDeleteReservationId = id"
      @show-comments="({ id }) => showPartnerSaleReservationCommentsId = id"
    />
  </div>
</template>

<style scoped>
.listContainer :deep(.isDeleted td) {
  @apply !bg-red-100
}

.listContainer :deep(.isActive td) {
  @apply !bg-green-100
}

.listContainer :deep(.isExpired td) {
  @apply !bg-gray-100
}
</style>
